import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, MoreVertical } from 'lucide-react';
import { siteConfig } from '@/siteConfig';
import { useLanguage } from '@/contexts/LanguageContext';
import { ThemeToggle } from './ThemeToggle';
import { LanguageSwitcher } from './LanguageSwitcher';
import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';

export const Header = () => {
  const { language, t } = useLanguage();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const navItems = siteConfig.nav[language];
  const routes = siteConfig.routes[language];

  return (
    <header className="fixed top-0 left-0 right-0 z-50 w-full border-b bg-background/95 backdrop-blur-md shadow-sm" data-testid="header">
      <div className="container mx-auto px-4 md:px-8 lg:px-12">
        <div className="flex h-14 items-center justify-between gap-4">
          {/* Logo - Left */}
          <Link to="/" className="flex flex-col items-start flex-shrink-0" data-testid="logo-link">
            <div className="font-heading font-bold tracking-tight leading-none">
              <span className="text-primary text-lg md:text-xl">Ng</span>
              <span className="text-accent text-lg md:text-xl">Sys</span>
            </div>
            <div className="text-xs text-muted-foreground font-medium tracking-wide mt-0.5">
              Automatisation
            </div>
          </Link>

          {/* Desktop Navigation (1200px+) - Everything visible and centered */}
          <nav className="hidden full:flex items-center justify-center flex-1" data-testid="desktop-nav-full">
            {navItems.map((item, index) => (
              <Link
                key={item}
                to={routes[index]}
                data-testid={`nav-link-${routes[index]}`}
                className={`text-base font-medium transition-colors hover:text-primary px-4 xl:px-5 ${
                  location.pathname === routes[index]
                    ? 'text-primary font-semibold'
                    : 'text-muted-foreground'
                }`}
              >
                {item}
              </Link>
            ))}
          </nav>

          {/* Desktop: Lang + Theme visible (1200px+) - Right */}
          <div className="hidden full:flex items-center space-x-3 flex-shrink-0">
            <LanguageSwitcher />
            <ThemeToggle />
          </div>

          {/* Tablet: Navigation centered + 3 dots right (900-1199px) */}
          <nav className="hidden nav:flex full:hidden items-center justify-center flex-1">
            {navItems.map((item, index) => (
              <Link
                key={item}
                to={routes[index]}
                className={`text-sm font-medium transition-colors hover:text-primary px-3 ${
                  location.pathname === routes[index]
                    ? 'text-primary font-semibold'
                    : 'text-muted-foreground'
                }`}
              >
                {item}
              </Link>
            ))}
          </nav>
          
          <div className="hidden nav:flex full:hidden flex-shrink-0">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="p-2" data-testid="settings-toggle">
                  <MoreVertical className="h-5 w-5" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="p-4 space-y-4">
                <div>
                  <p className="text-xs font-semibold mb-2 text-muted-foreground">Language</p>
                  <LanguageSwitcher />
                </div>
                <div>
                  <p className="text-xs font-semibold mb-2 text-muted-foreground">Theme</p>
                  <ThemeToggle />
                </div>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          {/* Mobile: Hamburger + 3 dots (<900px) - Right */}
          <div className="flex nav:hidden items-center gap-2 flex-shrink-0">
            <button
              className="p-2"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-toggle"
            >
              {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
            
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="p-2" data-testid="mobile-settings-toggle">
                  <MoreVertical className="h-5 w-5" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="p-4 space-y-4">
                <div>
                  <p className="text-xs font-semibold mb-2 text-muted-foreground">Language</p>
                  <LanguageSwitcher />
                </div>
                <div>
                  <p className="text-xs font-semibold mb-2 text-muted-foreground">Theme</p>
                  <ThemeToggle />
                </div>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        {/* Mobile Navigation Menu (<900px) */}
        {mobileMenuOpen && (
          <div className="nav:hidden py-4 space-y-2 border-t" data-testid="mobile-nav">
            {navItems.map((item, index) => (
              <Link
                key={item}
                to={routes[index]}
                onClick={() => setMobileMenuOpen(false)}
                className={`block px-4 py-2 text-sm font-medium transition-colors hover:text-primary rounded ${
                  location.pathname === routes[index]
                    ? 'text-primary font-semibold bg-primary/5'
                    : 'text-muted-foreground'
                }`}
              >
                {item}
              </Link>
            ))}
          </div>
        )}
      </div>
    </header>
  );
};
